'use client';
import { FieldTypes, StandardFormDto, StandardFormFieldDto } from '@app/models/dtos/form';
import { WorkspaceDto } from '@app/models/dtos/workspace-dto';

import { extractTextfromJSON } from './richTextEditorExtenstion/get-html-from-json';

export default function getFormShareURL(form: StandardFormDto, workspace: WorkspaceDto, defaultLink: boolean = false) {
    const slug = form?.settings?.customUrl || form?.formId;
    const scheme = window.PUBLIC_CONFIG?.HTTP_SCHEME;
    let domain = '';

    if (workspace?.isPro && workspace?.customDomain && !defaultLink && workspace?.customDomainVerified) {
        domain = workspace.customDomain;
    } else {
        domain = form?.builderVersion === 'v2' ? window.PUBLIC_CONFIG?.FORM_DOMAIN : window.PUBLIC_CONFIG?.FORM_DOMAIN;
    }

    const url = workspace?.isPro && workspace?.customDomain && !defaultLink && workspace?.customDomainVerified ? `/forms/${slug}` : `/${workspace.workspaceName}/forms/${slug}`;

    return `${scheme}${domain}${url}`;
}

export function getPlaceholderValueForField(fieldType?: FieldTypes) {
    switch (fieldType) {
        case FieldTypes.EMAIL:
            return 'name@example.com';
        case FieldTypes.NUMBER:
            return '123';
        case FieldTypes.SHORT_TEXT:
            return 'Answer';
        case FieldTypes.LINK:
            return 'https://';
        case FieldTypes.PHONE_NUMBER:
            return '0123456789';
        default:
            return 'No Field Selected';
    }
}

/**
 * What an empty read-only question shows where a box shows its prompt: a dash,
 * not "Answer" (nothing can be answered there). Where it is page text rather
 * than an input's placeholder, mark it `aria-hidden`: it is decoration, and a
 * screen reader would read it as "en dash".
 */
export const READ_ONLY_EMPTY = '\u2013';

/**
 * The placeholder a respondent sees when the creator set none, in the
 * respondent's language (`t` from useRespondentLanguage). Number, link and
 * phone examples read the same in every language.
 */
export function getRespondentPlaceholder(fieldType: FieldTypes | undefined, t: (key: string) => string): string {
    switch (fieldType) {
        case FieldTypes.SHORT_TEXT:
        case FieldTypes.LONG_TEXT:
            return t('FIELD.PLACEHOLDER_ANSWER');
        case FieldTypes.EMAIL:
            return t('FIELD.PLACEHOLDER_EMAIL');
        default:
            return getPlaceholderValueForField(fieldType);
    }
}

const IgnoredResponsesFieldType = [FieldTypes.TEXT, null, FieldTypes.IMAGE_CONTENT, FieldTypes.VIDEO_CONTENT];

/** The respondent-answered questions of a v2 form (internal fields excluded — see utils/internal-fields). */
export const getFieldsFromV2Form = (form: StandardFormDto): StandardFormFieldDto[] => {
    const fields = form.fields.map((slide) => {
        const filteredFields = slide?.properties?.fields?.filter((field: StandardFormFieldDto) => !IgnoredResponsesFieldType.includes(field.type) && !field?.internal);
        const filteredFieldsWithMatrix = filteredFields?.map((field: StandardFormFieldDto) => {
            if (field?.type !== FieldTypes.MATRIX) {
                return field;
            }
            const matrixRows = field?.properties?.fields?.map((row: StandardFormFieldDto) => {
                return {
                    ...row,
                    title: `${extractTextfromJSON(field)}[${row.title || `Row ${row.index + 1}`}]`
                };
            });
            return matrixRows;
        });
        return filteredFieldsWithMatrix;
    });
    return fields.flat(2)?.filter((field: any) => typeof field !== 'undefined') as StandardFormFieldDto[];
};
